import { z } from "zod";
import { flag, unset } from "./fields";
import { recordId } from "./contracts";

export const appointmentStatus = z.enum([
  "scheduled",
  "assigned",
  "accepted",
  "in_progress",
  "finished",
  "completed",
  "cancelled",
  "go_back_requested",
  "rescheduled",
  "inspected",
]);
export type AppointmentStatus = z.infer<typeof appointmentStatus>;

export const APPOINTMENT_STATUS_OF_ROW = {
  pending: "scheduled",
  assigned: "assigned",
  accepted: "accepted",
  inprogress: "in_progress",
  done: "finished",
  completed: "completed",
  cancelled: "cancelled",
  goback: "go_back_requested",
  rescheduled: "rescheduled",
  inspected: "inspected",
} as const satisfies Record<string, AppointmentStatus>;
export type AppointmentRowStatus = keyof typeof APPOINTMENT_STATUS_OF_ROW;

export const ROW_STATUS_OF_APPOINTMENT_STATUS = Object.fromEntries(
  Object.entries(APPOINTMENT_STATUS_OF_ROW).map(([row, status]) => [status, row]),
) as Record<AppointmentStatus, AppointmentRowStatus>;

const named = z.object({ id: recordId, name: z.string() });

export const paymentStatus = z.enum(["pending", "paid"]);
export type PaymentStatus = z.infer<typeof paymentStatus>;

const payRecord = z.object({ amount: z.number().nullable(), status: paymentStatus.nullable() });

export const storedExtraPhoto = z.object({ url: z.string(), is_after: z.boolean(), taken_at: z.string().nullable(), user: z.string().nullable() });
export type StoredExtraPhoto = z.infer<typeof storedExtraPhoto>;

export const storedWorkSnapshot = z.record(z.string(), z.unknown());
export type StoredWorkSnapshot = z.infer<typeof storedWorkSnapshot>;

const clockRecord = z.object({
  at: z.string(),
  photoUrl: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

export type StoredClock = { time: string; photoUrl: string; lat?: number; lng?: number };

export const storedClock = (clock: z.infer<typeof clockRecord> | null): StoredClock | undefined =>
  clock
    ? {
        time: clock.at,
        photoUrl: clock.photoUrl ?? "",
        ...(clock.latitude === null ? {} : { lat: clock.latitude }),
        ...(clock.longitude === null ? {} : { lng: clock.longitude }),
      }
    : undefined;

export const missingForCompletion = z.enum(["photos", "checklist", "price"]);
export type MissingForCompletion = z.infer<typeof missingForCompletion>;

export const billingException = z.object({ missing: z.array(missingForCompletion).min(1) });
export type BillingException = z.infer<typeof billingException>;

export const appointment = z.object({
  id: recordId,
  status: appointmentStatus,
  date: z.string(),
  time: z.string().nullable(),
  property: z.object({
    id: recordId,
    name: z.string(),
    address: z.string().nullable(),
    image: z.string().nullable(),
    accessNotes: z.string().nullable(),
    onSiteContact: z.object({ name: z.string(), phone: z.string().nullable() }).nullable(),
  }),
  customer: named.nullable().optional(),
  unit: z.object({ id: recordId, number: z.string(), beds: z.number().nullable(), baths: z.number().nullable() }).nullable(),
  vendor: named,
  service: z.object({
    id: recordId,
    name: z.string(),
    category: z.string().nullable(),
  }),
  assignment: z.object({ subcontractor: named.nullable(), worker: named.nullable() }),
  price: z.number().nullable().optional(),
  pay: z.object({ subcontractor: payRecord.optional(), worker: payRecord.optional() }),
  workOrderNotes: z.string().nullable(),
  createdAt: z.string(),
  workStartedAt: z.string().nullable(),
  clockIn: clockRecord.nullable(),
  clockOut: clockRecord.nullable(),
  finishedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  goBackRequestedAt: z.string().nullable(),
  cancellation: z.object({ at: z.string().nullable(), by: named.nullable(), note: z.string().nullable() }).nullable(),
  reassignmentRequest: z.object({ at: z.string().nullable(), byUserId: recordId.nullable(), reason: z.string() }).nullable(),
  extraPhotos: z.array(storedExtraPhoto).optional(),
  previousWork: z.array(storedWorkSnapshot).optional(),
  billingException: billingException.nullable().optional(),
});
export type Appointment = z.infer<typeof appointment>;

export const photoUrl = z.string().url().max(2048);

export const whereTaken = {
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
};

export const clockPhoto = z.object({ photoUrl, ...whereTaken }).strict();
export type ClockPhoto = z.infer<typeof clockPhoto>;

export const clockPhotoRequest = z
  .object({
    clock: z.enum(["in", "out"]),
    photoUrl,
    ...whereTaken,
    takenAt: z.string().datetime({ offset: true }).optional(),
  })
  .strict();
export type ClockPhotoRequest = z.infer<typeof clockPhotoRequest>;

export const extraPhotoRequest = z.object({ photoUrl, isAfter: z.boolean() }).strict();
export type ExtraPhotoRequest = z.infer<typeof extraPhotoRequest>;

export const extraPhotoRemoval = z.object({ photoUrl }).strict();
export type ExtraPhotoRemoval = z.infer<typeof extraPhotoRemoval>;

export const appointmentSortKey = z.enum(["createdAt", "date", "status", "id", "property", "unit", "service", "vendor", "worker", "price"]);
export type AppointmentSortKey = z.infer<typeof appointmentSortKey>;

export const optional = <T extends z.ZodTypeAny>(schema: T) => z.preprocess(unset, schema.optional());

const name = z.string().max(200).refine((value) => !value.includes("\0"), "no NUL characters");

const NOT_STORABLE_IN_JSONB = /[\0\p{Cs}]/u;

export const HAND_BACK_REASON_MAX_LENGTH = 1000;

export const storableText = (maxLength: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(maxLength)
    .refine((value) => !NOT_STORABLE_IN_JSONB.test(value), "no NUL characters or unpaired surrogates");

export const handBackRequest = z
  .object({
    reason: storableText(HAND_BACK_REASON_MAX_LENGTH),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type HandBackRequest = z.infer<typeof handBackRequest>;

export const CANCELLATION_REASON_MAX_LENGTH = 1000;

const blank = (value: unknown) => (typeof value === "string" && value.trim() === "" ? undefined : value);

export const cancelRequest = z
  .object({
    reason: z.preprocess(blank, storableText(CANCELLATION_REASON_MAX_LENGTH).optional()),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type CancelRequest = z.infer<typeof cancelRequest>;

export const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD")
  .refine((value) => {
    const at = new Date(`${value}T00:00:00Z`);
    return !value.startsWith("0000") && !Number.isNaN(at.getTime()) && at.toISOString().startsWith(value);
  }, "no such day");

export const rescheduleRequest = z
  .object({
    date: day,
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type RescheduleRequest = z.infer<typeof rescheduleRequest>;

export const GO_BACK_REASONS_MAX_LENGTH = 1000;

export const goBackRequest = z
  .object({
    date: day,
    reasons: z.preprocess(blank, storableText(GO_BACK_REASONS_MAX_LENGTH).optional()),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type GoBackRequest = z.infer<typeof goBackRequest>;

export const COMPLETION_REASON_MAX_LENGTH = 1000;

export const completeRequest = z
  .object({
    reason: z.preprocess(blank, storableText(COMPLETION_REASON_MAX_LENGTH).optional()),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type CompleteRequest = z.infer<typeof completeRequest>;

export const markDoneRequest = z
  .object({
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type MarkDoneRequest = z.infer<typeof markDoneRequest>;

export const serviceChangeRequest = z
  .object({
    service: recordId.toLowerCase(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type ServiceChangeRequest = z.infer<typeof serviceChangeRequest>;

const WORK_ORDER_NOTES_MAX_LENGTH = 4000;

const workOrderNotes = z
  .string()
  .trim()
  .max(WORK_ORDER_NOTES_MAX_LENGTH)
  .refine((value) => !NOT_STORABLE_IN_JSONB.test(value), "no NUL characters or unpaired surrogates");

export const changeRequest = z
  .object({
    date: optional(day),
    unit: optional(recordId.toLowerCase()),
    service: optional(recordId.toLowerCase()),
    workOrderNotes: workOrderNotes.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict()
  .refine(
    ({ date, unit, service, workOrderNotes }) => [date, unit, service, workOrderNotes].some((field) => field !== undefined),
    "name a date, unit, service or work order notes to change",
  );
export type ChangeRequest = z.infer<typeof changeRequest>;

export const bookRequest = z
  .object({
    property: recordId.toLowerCase(),
    unit: recordId.toLowerCase(),
    service: recordId.toLowerCase(),
    date: day,
    vendor: optional(recordId.toLowerCase()),
    workOrderNotes: workOrderNotes.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
    bulkUploadRow: z.number().int().positive().optional(),
  })
  .strict();
export type BookRequest = z.infer<typeof bookRequest>;

export const PAY_MAX = 1_000_000;

export const inWholeCents = (value: number) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6;

const money = z.number().min(0).max(PAY_MAX).refine(inWholeCents, "at most two decimal places");

export const assignRequest = z
  .object({
    subcontractor: optional(recordId.toLowerCase()),
    worker: optional(recordId.toLowerCase()),
    pay: money.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict()
  .refine(({ subcontractor, worker }) => (subcontractor === undefined) !== (worker === undefined), "name a subcontractor or a worker, not both");
export type AssignRequest = z.infer<typeof assignRequest>;

export const unassignRequest = z
  .object({
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type UnassignRequest = z.infer<typeof unassignRequest>;

export const payRequest = z
  .object({
    subcontractorPay: money.optional(),
    workerPay: money.optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict()
  .refine(({ subcontractorPay, workerPay }) => (subcontractorPay === undefined) !== (workerPay === undefined), "name a subcontractor pay or a worker pay, not both");
export type PayRequest = z.infer<typeof payRequest>;

export const ADJUST_REASON_MAX_LENGTH = 500;

export const adjustRequest = z
  .object({
    service: optional(recordId.toLowerCase()),
    price: money.optional(),
    pay: money.optional(),
    reason: z.preprocess(blank, storableText(ADJUST_REASON_MAX_LENGTH).optional()),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict()
  .refine(({ service, price, pay }) => [service, price, pay].some((field) => field !== undefined), "name a service, price or pay to adjust");
export type AdjustRequest = z.infer<typeof adjustRequest>;

const commaSeparated = (value: unknown) => (typeof value === "string" ? (value === "" ? undefined : value.split(",")) : value);

export const appointmentFilters = z.object({
  actingOrganization: optional(recordId.toLowerCase()),
  status: z.preprocess(commaSeparated, z.array(appointmentStatus).min(1).optional()),
  property: optional(recordId),
  vendorName: optional(name),
  assigneeName: optional(name),
  workerName: optional(name),
  serviceCategory: optional(name),
  dateFrom: optional(day),
  dateTo: optional(day),
  search: z.preprocess((value) => (typeof value === "string" ? unset(value.trim()) : value), name.optional()),
  searchVendor: z.preprocess(flag, z.boolean().default(true)),
});
export type AppointmentFilters = z.infer<typeof appointmentFilters>;

export const appointmentListQuery = appointmentFilters.extend({
  sort: z.preprocess(unset, appointmentSortKey.default("createdAt")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("desc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(100).default(25)),
});
export type AppointmentListQuery = z.infer<typeof appointmentListQuery>;

export const appointmentPage = z.object({
  items: z.array(appointment),
  total: z.number().int().nonnegative(),
});
export type AppointmentPage = z.infer<typeof appointmentPage>;

const count = z.number().int().nonnegative();
export const appointmentCounts = z.object({
  total: count,
  byStatus: z.object(Object.fromEntries(appointmentStatus.options.map((status) => [status, count])) as Record<AppointmentStatus, typeof count>),
  withGoBackRequestedAt: count,
});
export type AppointmentCounts = z.infer<typeof appointmentCounts>;

export const appointmentFilterOptions = z.object({
  vendorNames: z.array(z.string()),
  serviceCategories: z.array(z.string()),
  workerNames: z.array(z.string()),
  subcontractorNames: z.array(z.string()),
});
export type AppointmentFilterOptions = z.infer<typeof appointmentFilterOptions>;
